import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Close, Https, Link, People, VerifiedUser } from '@mui/icons-material';
import { Box, IconButton, List, ListItemText, Typography } from '@mui/material';

import { Avatar } from '@/ui/Avatar';
import { Drawer } from '@/ui/Drawer';
import { MenuListItem } from '@/ui/MenuListItem';

import { ReactComponent as DoubleArrow } from '@/icons/double-arrow.svg';
import { ReactComponent as OdrabiamyLogo } from '@/icons/odrabiamy.svg';

import { useProfile } from './ProfileProvider';
import SocialLinks from './SocialLinks';
import { routeConfig } from './routeConfig';
import { useHashRouting } from './utils/useHashRouting';

type MenuProps = {
  onDrawerOpen: (open: boolean) => void;
  isDrawerOpen: boolean;
  isDesktop: boolean;
};

export default function Menu({
  isDesktop,
  isDrawerOpen,
  onDrawerOpen,
}: MenuProps) {
  const { t } = useTranslation('menu');
  const [drawerOpenDelayed, setDrawerOpenDelayed] = useState(isDesktop);

  const { profile } = useProfile();
  const { isActive, setActive } = useHashRouting(routeConfig.profile);

  const shouldShrink = isDesktop ? !drawerOpenDelayed : false;

  const getToggleIconButton = () => {
    if (!isDesktop) {
      return <Close />;
    }

    return isDrawerOpen ? (
      <DoubleArrow style={{ transform: 'rotate(180deg)' }} />
    ) : (
      <DoubleArrow />
    );
  };

  const getReturnHref = () => {
    const fallbackUri = 'https://odrabiamy.pl/';
    const params = new URLSearchParams(window.location.search);
    return params.get('referrer_uri') || fallbackUri;
  };

  return (
    <Drawer
      onTransitionEnd={() => {
        setDrawerOpenDelayed(isDrawerOpen);
      }}
      anchor="left"
      variant={isDesktop ? 'permanent' : 'temporary'}
      open={isDrawerOpen}
      autoFocus
    >
      <IconButton
        sx={{
          justifyContent: isDrawerOpen ? 'flex-end' : 'center',
          paddingRight: isDrawerOpen ? 2 : 1,
        }}
        disableRipple
        onClick={() => {
          onDrawerOpen(!isDrawerOpen);
          if (isDrawerOpen) {
            setDrawerOpenDelayed(false);
          }
        }}
      >
        {getToggleIconButton()}
      </IconButton>
      <List sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
        {isDesktop && (
          <MenuListItem
            shrink={shouldShrink}
            active={false}
            sx={{ marginBottom: 2 }}
            buttonStyles={{
              height: shouldShrink ? 80 : 64,
            }}
            onClick={() => {
              setActive(routeConfig.profile);
              if (!isDesktop) {
                onDrawerOpen(false);
              }
            }}
          >
            <Avatar size="dense" />
            {!shouldShrink && (
              <Box sx={{ overflow: 'hidden' }}>
                <Typography
                  sx={{
                    fontWeight: (theme) => theme.typography.fontWeightBold,
                  }}
                  variant="subtitle1"
                >
                  {t('welcome')}
                </Typography>
                <Typography
                  variant="body2"
                  color="secondary"
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {profile?.email}
                </Typography>
              </Box>
            )}
          </MenuListItem>
        )}
        <MenuListItem
          shrink={shouldShrink}
          onClick={() => {
            setActive(routeConfig.profile);
            if (!isDesktop) {
              onDrawerOpen(false);
            }
          }}
          active={isActive(routeConfig.profile)}
        >
          <People />
          <ListItemText>{t('account')}</ListItemText>
        </MenuListItem>
        <MenuListItem
          shrink={shouldShrink}
          onClick={() => {
            setActive(routeConfig.password);
            if (!isDesktop) {
              onDrawerOpen(false);
            }
          }}
          active={isActive(routeConfig.password)}
        >
          <Https />
          <ListItemText>{t('password')}</ListItemText>
        </MenuListItem>
        <MenuListItem
          shrink={shouldShrink}
          onClick={() => {
            setActive(routeConfig.linkAccount);
            if (!isDesktop) {
              onDrawerOpen(false);
            }
          }}
          active={isActive(routeConfig.linkAccount)}
        >
          <Link />
          <ListItemText>
            {!shouldShrink ? t('linkAccount') : t('linkAccountShort')}
          </ListItemText>
        </MenuListItem>
        <MenuListItem
          shrink={shouldShrink}
          onClick={() => {
            setActive(routeConfig.activeSessions);
            if (!isDesktop) {
              onDrawerOpen(false);
            }
          }}
          active={isActive(routeConfig.activeSessions)}
        >
          <VerifiedUser />
          <ListItemText>
            {!shouldShrink ? t('activeSessions') : t('activeSessionsShort')}
          </ListItemText>
        </MenuListItem>

        <MenuListItem shrink={shouldShrink} sx={{ marginTop: 'auto' }}>
          <Box
            component="a"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexDirection: shouldShrink ? 'column' : 'row',
              width: '100%',
            }}
            href={getReturnHref()}
          >
            <OdrabiamyLogo />
            <ListItemText>
              {!shouldShrink
                ? t('returnToHomepage')
                : t('returnToHomepageShort')}
            </ListItemText>
          </Box>
        </MenuListItem>
      </List>
      {!shouldShrink && <SocialLinks />}
    </Drawer>
  );
}
